import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  getApplicantConfig,
  DEFAULT_APPLICANT_CONFIG,
  labelFor,
  profileDocLabel,
} from "../../services/backend/ApplicantConfigService";

// Bitácora del aspirante, para el gestor. Espejo de PqrsdLogsDrawer, pero sobre
// la colección applicantAudit en vez de un events[] en el documento: ahí ya
// quedaba registrado todo — el registro público, cada guardado del aspirante y
// cada decisión del gestor — y no había nada en la interfaz que lo leyera.
// Se muestra del más reciente al más antiguo.

const ACTION_LABEL = {
  CREATE: "Registro creado",
  UPDATE: "Actualización del aspirante",
  APPROVE: "Documento validado",
  REJECT: "Documento rechazado",
  SELECT: "Seleccionado como contratista",
};

const ACTION_COLOR = {
  CREATE: "info",
  UPDATE: "info",
  APPROVE: "success",
  REJECT: "error",
  SELECT: "success",
};

// Estado documental → texto, para no mostrar la constante cruda.
const STATUS_LABEL = {
  PENDING: "Pendiente",
  COMPLETE: "Completo",
  INCOMPLETE: "Incompleto",
  VALIDATED: "Validado",
};

// Campos de relevantData que se muestran como líneas de contexto, en este orden
// y sólo cuando vienen. `format` adapta los que no se leen bien en crudo.
const EXTRA_KEYS = [
  ["callName", "Convocatoria"],
  ["stage", "Etapa"],
  ["step", "Paso"],
  ["scope", "Ámbito", (v) => (v === "profile" ? "Hoja de vida" : "Convocatoria")],
  ["status", "Decisión", (v) => STATUS_LABEL[v] || v],
  ["documentationStatus", "Estado documental", (v) => STATUS_LABEL[v] || v],
  ["completionPercentage", "Completitud", (v) => `${v}%`],
  ["profileDocumentationStatus", "Estado hoja de vida", (v) => STATUS_LABEL[v] || v],
  ["profileCompletionPercentage", "Completitud hoja de vida", (v) => `${v}%`],
  ["uploadedDocuments", "Archivos de convocatoria cargados"],
  ["uploadedProfileDocuments", "Archivos de hoja de vida cargados"],
  ["photoUpdated", "Foto de perfil", (v) => (v ? "Actualizada" : "Sin cambios")],
  ["contractCode", "Contrato"],
  ["fileId", "Archivo"],
];

function formatTs(value) {
  if (!value) return "—";
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

function eventTime(ev) {
  const raw = ev?.timestamp || null;
  if (!raw) return 0;
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  const t = d.getTime();
  return isNaN(t) ? 0 : t;
}

export default function ApplicantLogsDrawer({ open, onClose, logs, loading }) {
  // El nombre humano de un documento vive en config/applicants; sin esto la
  // bitácora mostraría la clave cruda ("antecedente_redam").
  const [cfg, setCfg] = useState(DEFAULT_APPLICANT_CONFIG);
  useEffect(() => { if (open) getApplicantConfig().then(setCfg); }, [open]);

  const sorted = useMemo(() => {
    const list = Array.isArray(logs) ? logs.slice() : [];
    return list.sort((a, b) => eventTime(b) - eventTime(a));
  }, [logs]);

  const docLabel = (type, scope) => {
    if (!type) return null;
    const label = scope === "profile" ? profileDocLabel(cfg, type) : labelFor(cfg, type);
    return label || type;
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }} role="presentation">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Logs del aspirante</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Cerrar logs">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Bitácora completa: registro, cargas del aspirante y decisiones sobre sus
          documentos (sólo administradores).
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay eventos registrados todavía.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {sorted.map((ev, idx) => {
              const action = ev.action || "EVENTO";
              const label = ACTION_LABEL[action] || action;
              // El intake público no tiene usuario autenticado: firma "public".
              const actor = ev.userId === "public"
                ? `${ev.userName || "Aspirante"} (portal)`
                : (ev.userName || "Sistema");
              const rd = ev.relevantData || {};
              const documento = docLabel(rd.type, rd.scope);
              return (
                <Box
                  key={ev.id || `${action}-${idx}`}
                  sx={{
                    borderLeft: 3,
                    borderColor: `${ACTION_COLOR[action] || "info"}.main`,
                    pl: 1.5,
                    py: 0.5,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip size="small" label={label} color={ACTION_COLOR[action] || "default"} variant="outlined" />
                    <Typography variant="caption" color="text.secondary">
                      {formatTs(ev.timestamp)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Por: {actor}
                  </Typography>
                  {documento && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Documento: {documento}
                    </Typography>
                  )}
                  {EXTRA_KEYS.map(([key, lbl, format]) => {
                    const val = rd[key];
                    if (val === undefined || val === null || val === "") return null;
                    return (
                      <Typography
                        key={key}
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", whiteSpace: "pre-wrap" }}
                      >
                        {lbl}: {format ? format(val) : String(val)}
                      </Typography>
                    );
                  })}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", mt: 0.25, fontFamily: "monospace", fontSize: 10 }}
                  >
                    {action}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
